/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Promo Countdown Banner PD component — static or flip-clock display style
'use client';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import type { Image } from '@/types';

interface TimeLeft {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
}

type CountdownValue = number | null;

function formatCountdownValue(value: CountdownValue): string {
    return value === null ? '--' : String(value).padStart(2, '0');
}

function calcTimeLeft(endDate: string): TimeLeft | null {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return null;
    return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
    };
}

// ─── Static card (original style) ───────────────────────────────────────────

function StaticCard({ value, label }: { value: CountdownValue; label: string }) {
    return (
        <div className="flex flex-col items-center justify-center bg-background rounded-ui px-2 sm:px-4 py-4 flex-1 min-w-0">
            <span className="text-4xl lg:text-5xl font-light leading-none tabular-nums text-foreground">
                {formatCountdownValue(value)}
            </span>
            <span className="text-[10px] tracking-[0.18em] uppercase mt-2 text-muted-foreground">{label}</span>
        </div>
    );
}

// ─── Flip card ───────────────────────────────────────────────────────────────

/*
 * Direct port of the reference flip clock (~/flipclock/src).
 * Key mechanics:
 *  - `shuffle` boolean toggles each tick; the two AnimatedCard elements swap
 *    between 'fold' (top flap, folds away 0→-180°) and 'unfold' (bottom flap,
 *    unfolds 180→0°) by toggling their className — no remounting needed.
 *  - Static upper/lower cards always show current/previous halves.
 *  - `transform-style: preserve-3d` on the animated cards is what makes the
 *    rotation visible as a real 3-D card flip rather than a flat squash.
 *  - Digit is centered in the full card height; overflow:hidden on the half-height
 *    panel clips it to show only the correct half.
 *
 * CSS classes df-fold / df-unfold live in base.css.
 */

const digitSpanStyle: React.CSSProperties = {
    // The card stage scales down on small screens via `.flip-card-stage`'s clamp() (base.css),
    // but a fixed 64px digit doesn't — so on mobile the number overflowed the shrunken white card.
    // Track the card with a matching clamp: the 9.5vw middle keeps the digit at the same ~0.63 of
    // card height it has at full size, and the 64px ceiling leaves desktop pixel-identical.
    fontSize: 'clamp(30px, 9.5vw, 64px)',
    fontWeight: 300,
    color: 'hsl(var(--foreground))',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
};

function FlipCard({ value, label }: { value: CountdownValue; label: string }) {
    const fmt = formatCountdownValue;
    const prevRef = useRef(value);
    const [shuffle, setShuffle] = useState(true);
    const [current, setCurrent] = useState(value);
    const [previous, setPrevious] = useState(value);

    useEffect(() => {
        if (value !== prevRef.current) {
            setPrevious(prevRef.current);
            setCurrent(value);
            setShuffle((s) => !s);
            prevRef.current = value;
        }
    }, [value]);

    const currentStr = fmt(current);
    const previousStr = fmt(previous);

    // Matching reference exactly:
    // When shuffle flips, digit1 (top animated card) shows previous and plays fold;
    // digit2 (bottom animated card) shows current and plays unfold — and vice versa.
    const digit1 = shuffle ? previousStr : currentStr;
    const digit2 = !shuffle ? previousStr : currentStr;
    const anim1 = shuffle ? 'df-fold' : 'df-unfold';
    const anim2 = !shuffle ? 'df-fold' : 'df-unfold';

    // Shared styles for static half-panels
    const halfPanel: React.CSSProperties = {
        display: 'flex',
        position: 'relative',
        justifyContent: 'center',
        width: '100%',
        height: '50%',
        overflow: 'hidden',
        background: '#fff',
        border: '1px solid #f0f0f0',
    };

    // Shared styles for animated flap cards
    const flapBase: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        left: 0,
        width: '100%',
        height: '50%',
        overflow: 'hidden',
        backfaceVisibility: 'hidden',
        background: '#fff',
    };

    return (
        <div className="flip-card-unit" style={{ display: 'flex', flexDirection: 'column', borderRadius: 4 }}>
            {/* Card stage — white bg, perspective set here */}
            <div
                className="flip-card-stage"
                style={{
                    display: 'block',
                    position: 'relative',
                    width: 120,
                    height: 100,
                    perspectiveOrigin: '50% 50%',
                    perspective: '300px',
                    background: '#fff',
                    borderRadius: '4px 4px 0 0',
                }}>
                {/* Static upper — current, top half (digit pushed down so bottom clips) */}
                <div
                    style={{
                        ...halfPanel,
                        alignItems: 'flex-end',
                        borderBottom: '0.5px solid #e0e0e0',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                    }}>
                    <span style={{ ...digitSpanStyle, transform: 'translateY(50%)' }}>{currentStr}</span>
                </div>

                {/* Static lower — previous, bottom half (digit pushed up so top clips) */}
                <div style={{ ...halfPanel, alignItems: 'flex-start', borderTop: '0.5px solid #e0e0e0' }}>
                    <span style={{ ...digitSpanStyle, transform: 'translateY(-50%)' }}>{previousStr}</span>
                </div>

                {/* Animated card 1 — alternates fold/unfold on each tick */}
                <div
                    className={anim1 === 'df-fold' ? 'df-fold' : 'df-unfold'}
                    style={{
                        ...flapBase,
                        ...(anim1 === 'df-fold'
                            ? {
                                  top: 0,
                                  alignItems: 'flex-end',
                                  transformOrigin: '50% 100%',
                                  borderTop: '0.5px solid #f0f0f0',
                                  borderRight: '0.5px solid #f0f0f0',
                                  borderBottom: '0.5px solid #e0e0e0',
                                  borderLeft: '0.5px solid #f0f0f0',
                                  borderTopLeftRadius: 4,
                                  borderTopRightRadius: 4,
                              }
                            : {
                                  top: '50%',
                                  alignItems: 'flex-start',
                                  transformOrigin: '50% 0%',
                                  borderTop: '0.5px solid #e0e0e0',
                                  borderRight: '0.5px solid #f0f0f0',
                                  borderBottom: '0.5px solid #f0f0f0',
                                  borderLeft: '0.5px solid #f0f0f0',
                              }),
                    }}>
                    <span
                        style={{
                            ...digitSpanStyle,
                            transform: anim1 === 'df-fold' ? 'translateY(50%)' : 'translateY(-50%)',
                        }}>
                        {digit1}
                    </span>
                </div>

                {/* Animated card 2 — alternates unfold/fold on each tick */}
                <div
                    className={anim2 === 'df-fold' ? 'df-fold' : 'df-unfold'}
                    style={{
                        ...flapBase,
                        ...(anim2 === 'df-fold'
                            ? {
                                  top: 0,
                                  alignItems: 'flex-end',
                                  transformOrigin: '50% 100%',
                                  borderTop: '0.5px solid #f0f0f0',
                                  borderRight: '0.5px solid #f0f0f0',
                                  borderBottom: '0.5px solid #e0e0e0',
                                  borderLeft: '0.5px solid #f0f0f0',
                                  borderTopLeftRadius: 4,
                                  borderTopRightRadius: 4,
                              }
                            : {
                                  top: '50%',
                                  alignItems: 'flex-start',
                                  transformOrigin: '50% 0%',
                                  borderTop: '0.5px solid #e0e0e0',
                                  borderRight: '0.5px solid #f0f0f0',
                                  borderBottom: '0.5px solid #f0f0f0',
                                  borderLeft: '0.5px solid #f0f0f0',
                              }),
                    }}>
                    <span
                        style={{
                            ...digitSpanStyle,
                            transform: anim2 === 'df-fold' ? 'translateY(50%)' : 'translateY(-50%)',
                        }}>
                        {digit2}
                    </span>
                </div>
            </div>

            {/* Label — below card, same white, separated by a thin border */}
            <div
                className="flip-card-label"
                style={{
                    width: 120,
                    background: '#fff',
                    borderTop: '1px solid #e8e8e8',
                    borderRadius: '0 0 4px 4px',
                    textAlign: 'center',
                    padding: '5px 0 7px',
                    fontSize: '0.6em',
                    fontWeight: 400,
                    color: '#222',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                }}>
                {label}
            </div>
        </div>
    );
}

// RGBARRY-CUSTOMIZATION (mobile CWV): memoize the cards so, on each 1s tick, only the unit whose
// value actually changed (usually just "seconds") re-renders — days/hrs/min skip re-render, and the
// heavier FlipCard skips its whole render + inline-style rebuild when its digit is unchanged.
const StaticCardMemo = memo(StaticCard);
const FlipCardMemo = memo(FlipCard);

// RGBARRY-CUSTOMIZATION (mobile CWV): the live countdown is isolated into its own component so the
// once-per-second state update re-renders ONLY the timer digits — not the parent banner (headline,
// CTA, and the high-priority LCP image). Previously the interval lived on the parent, so every tick
// reconciled the whole banner (including the LCP <DynamicImage>) ~once/second for the life of the
// page — pure main-thread waste that landed in the TBT window and competed with INP. SSR parity is
// preserved: the server and first client render both show the "--" placeholder (no Date.now() during
// render); ticking starts only after mount. Expiry is lifted to the parent via onExpire so the whole
// banner still hides when the countdown reaches zero.
function CountdownRow({ endDate, isFlip, onExpire }: { endDate: string; isFlip: boolean; onExpire: () => void }) {
    const [isHydrated, setIsHydrated] = useState(false);
    const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);
    // Hold onExpire in a ref so the interval effect depends only on endDate and never re-subscribes.
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;

    useEffect(() => {
        const tick = () => {
            const next = calcTimeLeft(endDate);
            setTimeLeft(next);
            if (next === null) onExpireRef.current();
        };
        tick();
        setIsHydrated(true);
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [endDate]);

    const displayedTime = isHydrated ? timeLeft : null;
    const CountdownCard = isFlip ? FlipCardMemo : StaticCardMemo;

    return (
        <div className="flex gap-1 sm:gap-2">
            <CountdownCard value={displayedTime?.days ?? null} label="Days" />
            <CountdownCard value={displayedTime?.hours ?? null} label="Hrs" />
            <CountdownCard value={displayedTime?.minutes ?? null} label="Min" />
            <CountdownCard value={displayedTime?.seconds ?? null} label="Sec" />
        </div>
    );
}

// ─── PD metadata ─────────────────────────────────────────────────────────────

/* v8 ignore start */
@Component('promoCountdownBanner', {
    name: 'Promo Countdown Banner',
    description:
        'Two-column promotional banner: headline + countdown timer + CTA on the left, authorable image on the right. Hides when timer reaches zero.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class PromoCountdownBannerMetadata {
    @AttributeDefinition({ name: 'Headline', type: 'string' })
    headline?: string;

    @AttributeDefinition({
        id: 'headlineImage',
        name: 'Headline Image',
        description:
            'Optional image that replaces the text headline (e.g. a logo or lockup). When set, it renders in place of the Headline text; leave empty to use the Headline text.',
        type: 'image',
    })
    headlineImage?: Image;

    @AttributeDefinition({
        id: 'headlineImageAlt',
        name: 'Headline Image Alt Text',
        description: 'Alt text for the headline image. Falls back to the Headline text when empty.',
        type: 'string',
    })
    headlineImageAlt?: string;

    @AttributeDefinition({ id: 'subCopy', name: 'Sub Copy', type: 'string' })
    subCopy?: string;

    @AttributeDefinition({
        id: 'endDate',
        name: 'End Date',
        description: 'ISO 8601 date-time the countdown targets, e.g. 2026-12-31T23:59:59',
        type: 'string',
    })
    endDate?: string;

    @AttributeDefinition({ id: 'ctaText', name: 'CTA Text', type: 'string' })
    ctaText?: string;

    @AttributeDefinition({ id: 'ctaLink', name: 'CTA Link', type: 'url' })
    ctaLink?: string;

    @AttributeDefinition({ id: 'image', name: 'Image (right column)', type: 'image' })
    image?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background Color',
        description: 'Hex color for the banner background, e.g. #EDE8E0. Leave empty for the default muted tone.',
        type: 'string',
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'headlineColor',
        name: 'Headline Font Color',
        description: 'Hex color for the headline text, e.g. #1A1A1A. Leave empty for the default foreground color.',
        type: 'string',
    })
    headlineColor?: string;

    @AttributeDefinition({
        id: 'subCopyColor',
        name: 'Sub Copy Font Color',
        description: 'Hex color for the sub copy text, e.g. #6B6B6B. Leave empty for the default muted color.',
        type: 'string',
    })
    subCopyColor?: string;

    @AttributeDefinition({
        id: 'ctaTextColor',
        name: 'CTA Font Color',
        description: 'Hex color for the CTA button label text, e.g. #FFFFFF. Leave empty for the default.',
        type: 'string',
    })
    ctaTextColor?: string;

    @AttributeDefinition({
        id: 'ctaButtonColor',
        name: 'CTA Button Color',
        description: 'Hex color for the CTA button background, e.g. #1A1A1A. Leave empty for the default.',
        type: 'string',
    })
    ctaButtonColor?: string;

    @AttributeDefinition({
        id: 'displayStyle',
        name: 'Timer Style',
        description: 'static — plain number cards (default); flip — mechanical flip-clock animation.',
        type: 'enum',
        values: ['static', 'flip'],
    })
    displayStyle?: string;
}
/* v8 ignore stop */

// ─── Component ───────────────────────────────────────────────────────────────

export default function PromoCountdownBanner({
    headline,
    headlineImage,
    headlineImageAlt,
    subCopy,
    endDate,
    ctaText,
    ctaLink,
    image,
    imageAlt,
    backgroundColor,
    headlineColor,
    subCopyColor,
    ctaTextColor,
    ctaButtonColor,
    displayStyle,
}: {
    headline?: string;
    headlineImage?: Image;
    headlineImageAlt?: string;
    subCopy?: string;
    endDate?: string;
    ctaText?: string;
    ctaLink?: string;
    image?: Image;
    imageAlt?: string;
    backgroundColor?: string;
    headlineColor?: string;
    subCopyColor?: string;
    ctaTextColor?: string;
    ctaButtonColor?: string;
    displayStyle?: string;
}) {
    // RGBARRY-CUSTOMIZATION (mobile CWV): the once-per-second tick now lives in <CountdownRow> so the
    // banner shell (headline, CTA, and the high-priority LCP image) renders once instead of every
    // second. We only track expiry here — lifted from the row via onExpire — to hide the whole banner
    // when the countdown reaches zero, matching the previous behavior. `expired` starts false so the
    // server and first client render both show the banner (SSR parity), exactly as before.
    const [expired, setExpired] = useState(false);
    const handleExpire = useCallback(() => setExpired(true), []);

    if (expired) return null;

    const bgStyle = backgroundColor ? { backgroundColor } : undefined;
    const isFlip = displayStyle === 'flip';

    return (
        <div className="w-full bg-muted overflow-x-clip" style={bgStyle}>
            <div className="section-container px-6 lg:px-12 py-14 lg:py-20 grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                {/* Left column — copy, countdown, CTA */}
                <div className="flex flex-col gap-6">
                    <div>
                        {/* RGBARRY-CUSTOMIZATION: headline image replaces the text headline in the same slot when authored */}
                        {headlineImage?.url ? (
                            <DynamicImage
                                src={headlineImage.url}
                                alt={headlineImageAlt ?? headline ?? ''}
                                // Above the fold (top of the left column) so it loads eagerly, but it's the
                                // small wordmark — not the LCP. Kept off the high-priority preload lane so it
                                // doesn't contend with the right-column LCP image's high-priority preload.
                                priority="auto"
                                loading="eager"
                                widths={{ base: 420, lg: 480 }}
                                className="mb-2 block w-full max-w-[480px] [&_picture]:block [&_img]:h-auto [&_img]:w-full"
                            />
                        ) : (
                            headline && (
                                <h2
                                    style={{
                                        fontFamily: 'var(--font-display)',
                                        letterSpacing: '-0.03em',
                                        // RGBARRY-CUSTOMIZATION: author-selectable headline font color
                                        ...(headlineColor ? { color: headlineColor } : {}),
                                    }}
                                    className="text-5xl lg:text-[56px] font-light leading-[1.14] text-foreground mb-2">
                                    {headline}
                                </h2>
                            )
                        )}
                        {subCopy && (
                            // RGBARRY-CUSTOMIZATION: author-selectable sub copy font color
                            <p
                                className="text-sm text-muted-foreground leading-relaxed"
                                style={subCopyColor ? { color: subCopyColor } : undefined}>
                                {subCopy}
                            </p>
                        )}
                    </div>

                    {endDate && <CountdownRow endDate={endDate} isFlip={isFlip} onExpire={handleExpire} />}

                    {ctaText && ctaLink && (
                        <div>
                            <Button
                                asChild
                                // RGBARRY-CUSTOMIZATION: author-selectable CTA button background + label font color
                                style={
                                    ctaButtonColor || ctaTextColor
                                        ? { backgroundColor: ctaButtonColor, color: ctaTextColor }
                                        : undefined
                                }
                                className="df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 uppercase tracking-[0.12em] text-xs font-bold">
                                <Link to={ctaLink}>{ctaText}</Link>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Right column — image */}
                {image?.url && (
                    <div className="relative overflow-hidden rounded-ui aspect-[16/9] lg:aspect-[4/3] bg-muted/50">
                        <DynamicImage
                            src={image.url}
                            alt={imageAlt ?? ''}
                            priority="high"
                            // Mobile stacks to a single column ~full-width minus the px-6 gutter (~312–382px
                            // slot), but a 768px base made phones fetch the 1536w DPR2 candidate (~87KB). A
                            // 384px base emits a 384w/768w srcset, so a DPR2 phone picks 768w (~40KB) for its
                            // real slot; desktop keeps 720 for the half-width two-col grid. This is the LCP
                            // image, so it stays priority=high.
                            widths={{ base: 384, lg: 720 }}
                            className="absolute inset-0 w-full h-full"
                            imageProps={{ className: 'w-full h-full object-cover' }}
                        />
                        {/* RGBARRY-CUSTOMIZATION: stretched link makes the image clickable to the same
                            destination as the banner CTA, which remains independently clickable */}
                        {ctaText && ctaLink && <StretchedLink to={ctaLink} />}
                    </div>
                )}
            </div>
        </div>
    );
}
